import { useEffect, useState, useCallback } from 'react';
import { RefreshCw, ArrowUpDown, ArrowLeftRight, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatMoeda } from '@fiodecobre/shared';
import { ModalMovimentacaoCaixa, ModalTransferenciaCaixa, podeSerOrigemTransferencia } from '../../components/caixa/ModaisMovimentacaoCaixa';

type ContaFinanceira = {
  id: number;
  nome: string;
  tipo: string; // dinheiro | banco | digital | maquininha
  ordem: number;
  usuario_id: number | null;
  saldo: number;
  status: 'aberto' | 'fechado' | 'sem_sessao';
};

type Movimentacao = {
  ts: string;
  hora: string;
  tipo: string; // abertura | venda | sangria | suprimento
  descricao: string;
  valor: number;
};

type RespostaMovs = {
  escopo: string;
  truncado?: boolean;
  movimentacoes: Movimentacao[];
};

const ROTULO_TIPO: Record<string, string> = {
  abertura: 'Abertura',
  venda: 'Venda',
  sangria: 'Sangria',
  suprimento: 'Suprimento',
};

function BadgeStatus({ status }: { status: ContaFinanceira['status'] }) {
  if (status === 'aberto') {
    return (
      <span style={{ backgroundColor: '#fde68a', color: '#000', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99 }}>
        Aberto
      </span>
    );
  }
  if (status === 'fechado') {
    return (
      <span style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, border: '1px solid var(--input-border)' }}>
        Fechado
      </span>
    );
  }
  return <span style={{ color: 'var(--text-color)', opacity: 0.4, fontSize: 11 }}>Sem atividade</span>;
}

const estiloPainel: React.CSSProperties = {
  backgroundColor: 'var(--panel-bg)',
  border: '1px solid var(--input-border)',
  borderRadius: 10,
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const estiloCabecalho: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  padding: '14px 16px',
  borderBottom: '1px solid var(--input-border)',
  flex: '0 0 auto',
};

const estiloTitulo: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  opacity: 0.6,
};

const th: React.CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  opacity: 0.5,
  padding: '8px 12px',
  borderBottom: '1px solid var(--input-border)',
};

const td: React.CSSProperties = {
  padding: '9px 12px',
  borderBottom: '1px solid var(--input-border)',
  fontSize: 14,
};

const num: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

// Setas da fila manual — mesmo desenho da lista de Cotacoes (CotacoesHome.tsx):
// empilhadas na vertical, `var(--primary)`, 16px. Padrao unico de reordenar.
const estiloSeta: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  display: 'inline-flex',
};

export function FinanceiroPainel() {
  const { usuario } = useAuth();
  const [contas, setContas] = useState<ContaFinanceira[]>([]);
  const [selecionada, setSelecionada] = useState<number | null>(null);
  const [movs, setMovs] = useState<RespostaMovs | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [carregandoMovs, setCarregandoMovs] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Porta unica de entrada/saida — a direcao e escolhida DENTRO do modal (15/08/2026).
  const [modalMov, setModalMov] = useState(false);
  const [modalTransf, setModalTransf] = useState(false);

  // Mesmo gate da aba Sessoes do /caixa — a tela nao pode abrir uma porta de dinheiro
  // que o /caixa fecha pro mesmo usuario.
  const podeOperar = ['admin', 'gerente', 'caixa'].includes(usuario?.perfil ?? '') || usuario?.opera_caixa === true;
  const ehAdminOuGerente = ['admin', 'gerente'].includes(usuario?.perfil ?? '');
  // A ordem e compartilhada (vale pra todo mundo, em ~10 telas) — so admin/gerente mexe.
  const podeReordenar = ehAdminOuGerente;

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const data = await api.get<ContaFinanceira[]>('/api/financeiro/posicao');
      setContas(data);
      // Mantem a selecao entre recargas; so escolhe sozinho na primeira carga.
      setSelecionada((atual) => (atual !== null && data.some((c) => c.id === atual) ? atual : data[0]?.id ?? null));
    } catch {
      setErro('Não foi possível carregar a posição financeira.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Fila manual: sobe/desce uma casa e grava. Otimista — a lista se mexe na hora e
  // so volta atras se o servidor recusar (recarregar seria o unico jeito de saber
  // a ordem real). O backend recebe a lista INTEIRA e reescreve `ordem` = posicao.
  const mover = useCallback(async (id: number, delta: number) => {
    const idx = contas.findIndex((c) => c.id === id);
    const alvo = idx + delta;
    if (idx < 0 || alvo < 0 || alvo >= contas.length) return;

    const nova = [...contas];
    const [item] = nova.splice(idx, 1);
    nova.splice(alvo, 0, item!);
    setContas(nova);

    try {
      await api.put('/api/financeiro/reordenar', { ids: nova.map((c) => c.id) });
    } catch {
      setErro('Não foi possível salvar a nova ordem das contas.');
      void carregar();
    }
  }, [contas, carregar]);

  useEffect(() => {
    if (selecionada === null) { setMovs(null); return; }
    let cancelado = false;
    setCarregandoMovs(true);
    api.get<RespostaMovs>('/api/financeiro/movimentacoes', { query: { conta_id: selecionada } })
      .then((r) => { if (!cancelado) setMovs(r); })
      .catch(() => { if (!cancelado) setMovs({ escopo: '', movimentacoes: [] }); })
      .finally(() => { if (!cancelado) setCarregandoMovs(false); });
    return () => { cancelado = true; };
  }, [selecionada]);

  const total = contas.reduce((s, c) => s + c.saldo, 0);
  const contaSel = contas.find((c) => c.id === selecionada) ?? null;
  // Gaveta do proprio operador — vira a origem travada da transferencia pra quem nao e admin/gerente.
  const minhaGaveta = contas.find((c) => c.tipo === 'dinheiro' && c.usuario_id === usuario?.id) ?? null;

  return (
    <div className="page-content">
      <div className="pagina-header">
        <h2 className="pagina-titulo">Financeiro</h2>

        <div className="pagina-acoes">
        {podeOperar && (
          <>
            <button type="button" className="btn-secundario" onClick={() => setModalMov(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowUpDown size={15} /> Entrada / Saida
            </button>
            <button type="button" className="btn-secundario" onClick={() => setModalTransf(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowLeftRight size={15} /> Transferir
            </button>
          </>
        )}

        <button
          type="button"
          className="btn-secundario"
          onClick={carregar}
          disabled={carregando}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={15} className={carregando ? 'spin' : ''} />
          Atualizar
        </button>
        </div>
      </div>

      {erro && (
        <div style={{ background: '#ef4444', color: '#000', padding: '10px 16px', borderRadius: 8, marginBottom: 20, fontWeight: 600 }}>
          {erro}
        </div>
      )}

      {carregando && contas.length === 0 ? (
        <div style={{ color: 'var(--text-color)', opacity: 0.5, padding: 32, textAlign: 'center' }}>Carregando…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.35fr)', gap: 18, alignItems: 'stretch' }}>

          {/* mestre — contas */}
          <div style={estiloPainel}>
            <div style={estiloCabecalho}><span style={estiloTitulo}>Caixas e contas</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {podeReordenar && <th style={{ ...th, width: 34, padding: '8px 4px' }} />}
                  <th style={th}>Conta</th>
                  <th style={{ ...th, textAlign: 'right' }}>Saldo</th>
                  <th style={{ ...th, textAlign: 'center' }}>Situação</th>
                </tr>
              </thead>
              <tbody>
                {contas.map((c, idx) => {
                  const sel = c.id === selecionada;
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setSelecionada(c.id)}
                      style={{ cursor: 'pointer', ...(sel ? { backgroundColor: '#fde68a', color: '#000' } : {}) }}
                    >
                      {podeReordenar && (
                        <td style={{ ...td, padding: '4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <button
                              type="button"
                              title="Mover para cima"
                              onClick={() => void mover(c.id, -1)}
                              disabled={idx === 0}
                              style={{ ...estiloSeta, opacity: idx === 0 ? 0.25 : 1, cursor: idx === 0 ? 'default' : 'pointer' }}
                            >
                              <ChevronUp size={16} color="var(--primary)" />
                            </button>
                            <button
                              type="button"
                              title="Mover para baixo"
                              onClick={() => void mover(c.id, 1)}
                              disabled={idx === contas.length - 1}
                              style={{ ...estiloSeta, opacity: idx === contas.length - 1 ? 0.25 : 1, cursor: idx === contas.length - 1 ? 'default' : 'pointer' }}
                            >
                              <ChevronDown size={16} color="var(--primary)" />
                            </button>
                          </div>
                        </td>
                      )}
                      <td style={{ ...td, ...(sel ? { fontWeight: 700 } : {}) }}>{c.nome}</td>
                      <td style={{ ...td, ...num, fontWeight: 600 }}>{formatMoeda(c.saldo)}</td>
                      <td style={{ ...td, textAlign: 'center' }}><BadgeStatus status={c.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ flex: '1 1 auto' }} />
            <div style={{ backgroundColor: '#1e3a8a', color: '#fde68a', padding: '14px 12px', textAlign: 'right', whiteSpace: 'nowrap', flex: '0 0 auto' }}>
              <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 14 }}>
                Total em caixa
              </span>
              <span style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMoeda(total)}</span>
            </div>
          </div>

          {/* detalhe — movimentacoes da conta selecionada */}
          <div style={estiloPainel}>
            <div style={estiloCabecalho}>
              <span style={estiloTitulo}>
                {contaSel ? `Movimentações — ${contaSel.nome}` : 'Movimentações'}
              </span>
              <span style={{ fontSize: 12, opacity: 0.55 }}>{movs?.escopo ?? ''}</span>
            </div>
            <div style={{ flex: '1 1 auto', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Quando</th>
                    <th style={th}>Tipo</th>
                    <th style={th}>Descrição</th>
                    <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {carregandoMovs ? (
                    <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', opacity: 0.4, fontSize: 14 }}>Carregando…</td></tr>
                  ) : !movs || movs.movimentacoes.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', opacity: 0.4, fontSize: 14 }}>Nenhuma movimentação</td></tr>
                  ) : (
                    movs.movimentacoes.map((m, i) => (
                      <tr key={`${m.ts}-${i}`}>
                        <td style={{ ...td, ...num, textAlign: 'left', opacity: 0.6, fontSize: 13 }}>{m.hora}</td>
                        <td style={{ ...td, fontSize: 13 }}>{ROTULO_TIPO[m.tipo] ?? m.tipo}</td>
                        <td style={{ ...td, fontSize: 13, opacity: 0.75 }}>{m.descricao}</td>
                        <td style={{ ...td, ...num, fontWeight: 600 }}>{formatMoeda(m.valor)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {movs?.truncado && (
                <div style={{ padding: '10px 12px', fontSize: 12, opacity: 0.55, textAlign: 'center' }}>
                  Mostrando as 200 movimentações mais recentes.
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {modalMov && (
        <ModalMovimentacaoCaixa
          contas={contas}
          contaInicial={selecionada}
          onFechar={() => setModalMov(false)}
          onConcluido={() => void carregar()}
        />
      )}

      {modalTransf && (
        <ModalTransferenciaCaixa
          contas={contas}
          origemFixa={ehAdminOuGerente ? null : (minhaGaveta ? { id: minhaGaveta.id, nome: minhaGaveta.nome } : null)}
          // Pre-seleciona a conta marcada na lista quando ela pode MESMO ser origem: especie
          // sempre; conta bancaria so pra admin/gerente (mesma regra do select dentro do modal).
          origemInicial={contaSel && (podeSerOrigemTransferencia(contaSel) || ehAdminOuGerente) ? contaSel.id : null}
          onFechar={() => setModalTransf(false)}
          onConcluido={() => void carregar()}
        />
      )}
    </div>
  );
}
